import React, { useState } from 'react';
import { IAuditoriaCaixaFornecedoresResultado, IAuditoriaCaixaFornecedoresItem } from '../interfaces/IAuditoriaCaixaFornecedores';

interface Props {
  resultado: IAuditoriaCaixaFornecedoresResultado;
}

const formatCurrency = (value?: number) =>
  (value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const cls =
    status === 'ERRO'   ? 'badge-light-danger' :
    status === 'ALERTA' ? 'badge-light-warning' :
    status === 'AVISO'  ? 'badge-light-warning' :
                          'badge-light-success';
  return <span className={`badge ${cls}`}>{status}</span>;
};

const ItemRow: React.FC<{ item: IAuditoriaCaixaFornecedoresItem }> = ({ item }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr>
        <td>
          <button
            className='btn btn-sm btn-icon btn-light me-2'
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Recolher' : 'Expandir'}
          >
            <i className={`bi bi-chevron-${expanded ? 'up' : 'down'}`} />
          </button>
          <span className='fw-bold'>{item.empresaNome}</span>
          <br />
          <span className='text-muted fs-8'>{item.cnpj}</span>
        </td>
        <td className='fw-bold'>{item.dataLancamento}</td>
        <td className='text-muted fs-8' style={{ maxWidth: '200px', wordBreak: 'break-word' }}>
          {item.fornecedorHistorico}
        </td>
        <td>
          <span className='text-muted fs-8 d-block'>Caixa: {item.contaCaixa}</span>
          <span className='text-muted fs-8 d-block'>Forn.: {item.contaFornecedor}</span>
        </td>
        <td className='text-end fw-bold'>{formatCurrency(item.valor)}</td>
        <td className='text-end fw-bold text-danger'>{formatCurrency(item.passivoTributario)}</td>
        <td className='text-center'><StatusBadge status={item.status} /></td>
      </tr>

      {expanded && (
        <tr>
          <td colSpan={7} className='bg-light'>
            <div className='p-4'>
              <div className='row g-3 mb-3'>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>IRPJ/CSLL (34%)</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.tributosIrpjCsll)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Multa Art. 44 (75%)</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.multaArt44)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Juros SELIC</div>
                    <div className='fw-bolder fs-5 text-warning'>{formatCurrency(item.jurosSelic)}</div>
                  </div>
                </div>
                <div className='col-md-3'>
                  <div className='card border-0 bg-white shadow-sm p-3 text-center'>
                    <div className='text-muted fs-8'>Passivo Total</div>
                    <div className='fw-bolder fs-5 text-danger'>{formatCurrency(item.passivoTributario)}</div>
                  </div>
                </div>
              </div>
              <div className='alert alert-light-danger border border-danger p-3 fs-8'>
                {item.mensagemDiagnostico}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const AuditoriaCaixaFornecedoresDetalhe: React.FC<Props> = ({ resultado }) => {
  if (!resultado) return null;

  return (
    <div className='card card-custom card-stretch mb-5'>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bolder fs-4 mb-1'>
            Detalhamento — Pagamentos a Fornecedores via Caixa/Espécie
          </span>
          <span className='text-muted mt-1 fw-bold fs-7'>
            {resultado.itens.length} ocorrência(s) detectada(s) · Passivo total estimado:{' '}
            <strong className='text-danger'>{formatCurrency(resultado.passivoTributarioTotal)}</strong>
          </span>
        </h3>
      </div>

      {resultado.mensagemDirecionamento && (
        <div className='card-body pb-0'>
          <div className={`alert p-3 fs-8 ${
            resultado.status === 'ERRO'   ? 'alert-light-danger border border-danger' :
            resultado.status === 'ALERTA' ? 'alert-light-warning border border-warning' :
                                            'alert-light-warning border border-warning'
          }`}>
            {resultado.mensagemDirecionamento}
          </div>
        </div>
      )}

      <div className='card-body pt-3'>
        {resultado.itens.length === 0 ? (
          <div className='text-success fw-bold fs-5 text-center py-8'>
            ✓ Nenhum pagamento a fornecedor via caixa/espécie identificado. Situação regular.
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-3'>
              <thead>
                <tr className='fw-bolder text-muted'>
                  <th>Empresa</th>
                  <th>Data</th>
                  <th>Histórico</th>
                  <th>Contas</th>
                  <th className='text-end'>Valor</th>
                  <th className='text-end'>Passivo</th>
                  <th className='text-center'>Status</th>
                </tr>
              </thead>
              <tbody>
                {resultado.itens.map((item, i) => (
                  <ItemRow key={i} item={item} />
                ))}
              </tbody>
            </table>
          </div>
        )}

        {resultado.fundamentacaoLegal && (
          <div className='alert alert-light border border-secondary p-3 fs-9 mt-4'>
            <strong>Fundamentação Legal:</strong> {resultado.fundamentacaoLegal}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditoriaCaixaFornecedoresDetalhe;
